import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import InfoIcon from '@mui/icons-material/Info';

import SigsAutoSuggest from 'common/forms/SigsAutoSuggest';
import { syncSig } from 'store/thunks/workQueueThunks';
import { DirectionReviewReason, PrescriptionReviewStatus } from 'enums/prescription.enum';
import { SigLengthLimits } from 'enums/SigManagement.enum';
import { PrescriptionReviewPayloadContext } from 'common/hooks/usePrescriptionReviewPayloadContext';
import { isEmpty } from 'lodash';
import ConfirmDirectionReassignmentDialog from './ConfirmDirectionReassignmentDialog';
import DirectionReviewReasonBanner from './DirectionReviewReasonBanner';

const ResolveDirectionsOption = {
  APPROVE_CURRENT_SIG: 'APPROVE_CURRENT_SIG',
  CREATE_SIG_FROM_DIRECTION: 'CREATE_SIG_FROM_DIRECTION',
  ASSOCIATE_WITH_SIG: 'ASSOCIATE_WITH_SIG',
};

const tableCellStye = { borderBottom: 'none', p: 1 };

const ResolvePrescriptionModal = ({
  open,
  onClose,
  drug,
  strength,
  form,
  directions,
  prescriptionId,
  patientId,
  reviewType,
  lineItemId,
  reviewReason,
  currentSigId,
  currentSig,
}) => {
  const dispatch = useDispatch();
  const isDirectionReview = reviewType === PrescriptionReviewStatus.DIRECTION_REVIEW;

  /**
   * The direction already resolves to a sig; it is only the drug that has not been approved for it.
   * Approving that sig is the resolution, so it is offered and pre-selected. Searching for another
   * sig here is what gives the direction a second meaning and sends the next order straight back.
   *
   * Both the id and the text are required: the id is what is written, and the banner is where the
   * reviewer reads which sig they are approving.
   */
  const canApproveCurrentSig =
    isDirectionReview &&
    reviewReason === DirectionReviewReason.NOT_APPROVED_FOR_DRUG &&
    !!currentSigId &&
    !!currentSig;

  /**
   * Whether "Create Sig From Direction" can work at all, which is a question about length.
   *
   * A direction may run to 510 characters and a sig may not exceed 180, so a long direction cannot
   * become one and offering to make one out of it only produces a write that fails at the column.
   *
   * Both ends, and the same limits the server enforces. Checking only the maximum still offered the
   * option for a one- or two-character direction and preselected it, so the reviewer's first click
   * failed on the server's minimum with nothing on screen to explain it.
   */
  const directionLengthForASig = directions?.trim().length ?? 0;
  const canDirectionBecomeASig =
    directionLengthForASig >= SigLengthLimits.SIG_MIN &&
    directionLengthForASig <= SigLengthLimits.SIG_MAX;

  // The review data arrives after the first render, so the default cannot be captured in state --
  // it stays derived until the reviewer picks something.
  const [chosenOption, setChosenOption] = useState(null);
  const defaultOption = () => {
    if (canApproveCurrentSig) {
      return ResolveDirectionsOption.APPROVE_CURRENT_SIG;
    }

    // Skipped when it is disabled, or the modal opens with an option selected that cannot be
    // submitted — and the reviewer has to work out why the button does nothing.
    return canDirectionBecomeASig
      ? ResolveDirectionsOption.CREATE_SIG_FROM_DIRECTION
      : ResolveDirectionsOption.ASSOCIATE_WITH_SIG;
  };
  const reviewOption = chosenOption ?? defaultOption();
  const [loading, setLoading] = useState(false);
  const [selectedSig, setSelectedSig] = useState('');
  const [hasOneTimeAssociation, setOneTimeAssociation] = useState(false);
  const [reassignment, setReassignment] = useState(null);
  const { payload, setPayload } = useContext(PrescriptionReviewPayloadContext);

  const handleChangeRadio = (event) => {
    setChosenOption(event.target.value);
  };

  const handleOneTimeAssociation = () => setOneTimeAssociation(!hasOneTimeAssociation);

  const buildSigData = () => {
    if (reviewOption === ResolveDirectionsOption.APPROVE_CURRENT_SIG) {
      return { hasOneTimeAssociation, sigId: currentSigId, sig: currentSig };
    }

    if (reviewOption === ResolveDirectionsOption.CREATE_SIG_FROM_DIRECTION) {
      // Trimmed, and the same text the gate above measured. The gate reads the trimmed length while
      // the server's maximum is on the raw string, so sending it raw let a direction that had just
      // been judged short enough be refused for its own surrounding whitespace.
      return { hasOneTimeAssociation, sig: directions?.trim() };
    }

    return {
      hasOneTimeAssociation,
      sigId: selectedSig?.sigId,
      sig: selectedSig?.sig,
    };
  };

  const resolveDirection = (sigData, confirmDirectionReassignment) => {
    setLoading(true);

    return dispatch(
      syncSig({ prescriptionId, lineItemId, ...sigData, confirmDirectionReassignment })
    )
      .then((result) => {
        // The direction already means something else. Only the reviewer can decide whether to
        // change it, so the modal stays open and asks instead of reporting a failure.
        if (result?.needsDirectionReassignmentConfirmation) {
          setReassignment({ ...result, sigData });

          return;
        }

        if (result) {
          setReassignment(null);
          onClose();
          enqueueSnackbar('Prescription Direction resolved!', { variant: 'success' });
        }
      })
      .finally(() => setLoading(false));
  };

  const handleSyncSig = (data) => {
    if (isDirectionReview) {
      resolveDirection(buildSigData());
    } else {
      const overrideDrugProfilePayload = !isEmpty(payload)
        ? { ...payload, sig: data.searchExistingSig }
        : { patientId, sig: data.searchExistingSig };

      setPayload(overrideDrugProfilePayload);
      onClose();
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Approve Prescription Directions</DialogTitle>
      <Form
        onSubmit={handleSyncSig}
        render={({ handleSubmit, invalid }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent sx={{ minWidth: 550 }}>
              {isDirectionReview && (
                <DirectionReviewReasonBanner reviewReason={reviewReason} currentSig={currentSig} />
              )}

              <Grid>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tableCellStye, p: 0 }}>
                        <Typography sx={{ fontWeight: 600 }}>Drug</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <DrugInfo fieldKey='Name' fieldValue={drug} />
                    <DrugInfo fieldKey='Strength' fieldValue={strength} />
                    <DrugInfo fieldKey='Form' fieldValue={form} />
                    <DrugInfo fieldKey='Directions' fieldValue={directions} />
                  </TableBody>
                </Table>
              </Grid>

              {reviewType === PrescriptionReviewStatus.DIRECTION_REVIEW && (
                <Grid container direction='row' sx={{ p: 1, pl: 0 }}>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby='Review-Prescription-option-radioSelect-group'
                      value={reviewOption}
                      onChange={handleChangeRadio}
                    >
                      {canApproveCurrentSig && (
                        <FormControlLabel
                          value={ResolveDirectionsOption.APPROVE_CURRENT_SIG}
                          control={<Radio />}
                          label='Approve The Current Sig For This Drug'
                        />
                      )}
                      {/* A direction may be up to 510 characters and a sig may not exceed 180,
                          so this option cannot always be offered. It used to hand the
                          direction's text over as the sig regardless, and the write failed at
                          the column with a driver error the reviewer could do nothing with.

                          The reason is rendered, not put in a tooltip: a disabled control does
                          not fire the events a tooltip needs, so an explanation attached to one
                          never appears. */}
                      <FormControlLabel
                        value={ResolveDirectionsOption.CREATE_SIG_FROM_DIRECTION}
                        control={<Radio />}
                        disabled={!canDirectionBecomeASig}
                        label='Create Sig From Direction'
                      />
                      {/* Written out rather than a tooltip: a tooltip does not fire on a disabled
                          control, so the reason for the greyed-out option would be unreachable. */}
                      {!canDirectionBecomeASig && (
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ pl: 4, pb: 0.5, display: 'block' }}
                        >
                          {`This direction is ${directionLengthForASig} characters and a sig must be between ${SigLengthLimits.SIG_MIN} and ${SigLengthLimits.SIG_MAX}, so it cannot become one. Associate it with an existing sig instead.`}
                        </Typography>
                      )}
                      <FormControlLabel
                        value={ResolveDirectionsOption.ASSOCIATE_WITH_SIG}
                        control={<Radio />}
                        label='Associate With Sig'
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {(reviewOption === ResolveDirectionsOption.ASSOCIATE_WITH_SIG ||
                reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW) && (
                <SigsAutoSuggest
                  id='Review-Prescription-Existing-Sig-Input'
                  name='searchExistingSig'
                  label='Search Existing'
                  autoFocus
                  maxLength={SigLengthLimits.SIG_MAX}
                  selected={selectedSig}
                  handleSelect={setSelectedSig}
                />
              )}
            </DialogContent>

            <DialogActions>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  {isDirectionReview ? (
                    <Grid container alignItems='center'>
                      <FormControlLabel
                        label='One Time Association'
                        control={<Checkbox onChange={handleOneTimeAssociation} />}
                        sx={{ px: 0, mr: 1 }}
                      />
                      <Tooltip
                        title={
                          reviewOption === ResolveDirectionsOption.CREATE_SIG_FROM_DIRECTION
                            ? 'Applies to this order only. No sig is created and no direction is linked.'
                            : 'This association will apply only to the current case.'
                        }
                        arrow
                        color='secondary'
                        placement='top-start'
                        sx={{ px: 0 }}
                      >
                        <InfoIcon />
                      </Tooltip>

                      {hasOneTimeAssociation &&
                        reviewOption === ResolveDirectionsOption.CREATE_SIG_FROM_DIRECTION && (
                          <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                            No sig will be created.
                          </Typography>
                        )}
                    </Grid>
                  ) : null}
                </Grid>
                <Grid item>
                  <Button sx={{ mr: 1 }} variant='outlined' color='secondary' onClick={onClose}>
                    Cancel
                  </Button>

                  <LoadingButton
                    disabled={
                      invalid ||
                      (isEmpty(selectedSig) &&
                        reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW) ||
                      (reviewOption === ResolveDirectionsOption.ASSOCIATE_WITH_SIG &&
                        !selectedSig?.sigId)
                    }
                    loading={loading}
                    variant='contained'
                    type='submit'
                  >
                    {isDirectionReview ? 'Save' : 'Confirm'}
                  </LoadingButton>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      />

      <ConfirmDirectionReassignmentDialog
        open={!!reassignment}
        loading={loading}
        direction={directions}
        currentSig={reassignment?.currentSig}
        nextSig={reassignment?.sigData?.sig}
        onClose={() => setReassignment(null)}
        onConfirm={() => resolveDirection(reassignment.sigData, true)}
      />
    </Dialog>
  );
};

const DrugInfo = ({ fieldKey, fieldValue }) => (
  <TableRow>
    <TableCell sx={tableCellStye}>{fieldKey}:</TableCell>
    <TableCell sx={tableCellStye}>{fieldValue} </TableCell>
  </TableRow>
);

export default ResolvePrescriptionModal;

DrugInfo.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  fieldValue: PropTypes.string.isRequired,
};

ResolvePrescriptionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  drug: PropTypes.string.isRequired,
  directions: PropTypes.string.isRequired,
  strength: PropTypes.string,
  form: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  prescriptionId: PropTypes.string.isRequired,
  patientId: PropTypes.string,
  reviewType: PropTypes.string,
  lineItemId: PropTypes.string.isRequired,
  reviewReason: PropTypes.string,
  currentSigId: PropTypes.string,
  currentSig: PropTypes.string,
};

ResolvePrescriptionModal.defaultProps = {
  strength: 'NA',
  form: 'NA',
  patientId: '',
  reviewType: '',
  reviewReason: '',
  currentSigId: '',
  currentSig: '',
};
